import { resolveDisplayTimezone, sarah } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { orpcPublic, orpcPublicClient } from "@/lib/orpc";
import { SharedCallRefusal } from "@/routes/-components/shared-call-refusal";
import { SummaryPanel } from "@/routes/_authenticated/_front-desk/sarah/-components/call-detail-panels";
import { TranscriptPanel } from "@/routes/_authenticated/_front-desk/sarah/-components/transcript-panel";
import { TransferPanel } from "@/routes/_authenticated/_front-desk/sarah/-components/transfer-panel";
import { UnverifiedNotice } from "@/routes/_authenticated/_front-desk/sarah/-components/unverified-notice";
import {
  formatCallDuration,
  formatCallWhen,
} from "@/routes/_authenticated/_front-desk/sarah/-components/call-format";
import { SarahCallStatus } from "@/routes/_authenticated/_front-desk/sarah/-components/call-presentation";
import { CallRecordingPlayer } from "@/routes/_authenticated/_front-desk/sarah/-components/call-recording-player";
import { SharedCallShell } from "@/routes/-components/shared-call-shell";
import { SharedCallSection } from "@/routes/-components/shared-call-section";
import type { CallDetail } from "@/routes/_authenticated/_front-desk/sarah/-components/call-detail-types";

export const Route = createFileRoute("/shared/$callId/$token")({
  component: SharedCall,
  head: () => ({
    meta: [
      { title: "Call details" },
      { name: "robots", content: "noindex, nofollow" },
      // The signature is in the address. Nothing this page links to may learn it.
      { name: "referrer", content: "no-referrer" },
    ],
  }),
});

/** One call read without an account. The URL signature is the whole authorization, so only the id
 * and signature are sent; the server reads the call, building and clock from the row. "Open in app"
 * goes to the same call in the call log, via login if signed out. */
function SharedCall() {
  const { callId, token } = Route.useParams();
  const shared = useQuery({
    ...orpcPublic.frontDesk.calls.publicView.queryOptions({ input: { callId, token } }),
    retry: false,
  });

  if (shared.isPending) {
    return (
      <SharedCallShell callId={callId}>
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-64 w-full" />
      </SharedCallShell>
    );
  }
  if (shared.isError || !shared.data) {
    return (
      <SharedCallShell callId={callId}>
        <SharedCallRefusal callId={callId} error={shared.error} />
      </SharedCallShell>
    );
  }

  const view = shared.data;
  const timeZone = resolveDisplayTimezone(view.facilityTimezone);
  const outbound = view.direction === "outbound";
  const number = outbound
    ? sarah.callerNumberFrom(`phone:${view.toNumber ?? ""}`)
    : view.callerNumber;

  /* The panels read the call log's own shape. Everything the link does not
     carry is filled with the value that renders as nothing. */
  const call: CallDetail = {
    ...view,
    facilityId: "",
    triage: "open",
    events: [],
    recordingUrl: null,
    transferRecordingUrl: null,
    flags: view.callerUnverified ? ["unverified"] : [],
    staffName: null,
    staffSection: null,
    triagedAt: null,
    triagedVia: null,
    handledByName: null,
    residentId: null,
    residentName: null,
  };

  return (
    <SharedCallShell callId={callId}>
      <header className="flex flex-col gap-1">
        {view.facilityName ? (
          <p className="text-muted-foreground text-sm">{view.facilityName} front desk</p>
        ) : null}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="font-display text-xl">{formatCallWhen(view.startedAt, timeZone)}</h1>
          <SarahCallStatus status={view.status} direction={view.direction} />
        </div>
        <p className="text-muted-foreground flex flex-wrap items-center gap-1.5 text-sm">
          {number ? (
            <a href={`tel:${number}`} className="text-foreground font-mono underline">
              {outbound ? `Called ${number}` : number}
            </a>
          ) : (
            <span>{outbound ? "Called an extension" : "No caller number"}</span>
          )}
          <span className="tabular-nums">{formatCallDuration(view.durationSeconds)}</span>
        </p>
      </header>

      {view.callerUnverified ? <UnverifiedNotice /> : null}

      <SharedCallSection label="Recording">
        <Card>
          <CardContent className="p-4">
            {view.hasRecording ? (
              <CallRecordingPlayer
                callId={view.id}
                size="default"
                load={async () =>
                  (await orpcPublicClient.frontDesk.calls.publicRecording({ callId, token })).file
                }
              />
            ) : (
              <p className="text-muted-foreground text-sm text-pretty">
                {view.status === "active" || view.status === "summarizing"
                  ? "The recording appears here a few moments after the call ends. Refresh to check."
                  : "There is no recording for this call."}
              </p>
            )}
          </CardContent>
        </Card>
      </SharedCallSection>

      <SharedCallSection label="Summary">
        <SummaryPanel call={call} />
      </SharedCallSection>

      {view.transfer ? (
        <SharedCallSection label="Transfer">
          <TransferPanel call={call} timeZone={timeZone} />
        </SharedCallSection>
      ) : null}

      {Array.isArray(view.transcript) && view.transcript.length > 0 ? (
        <SharedCallSection label="Transcript">
          <TranscriptPanel call={call} />
        </SharedCallSection>
      ) : null}

      <p className="text-muted-foreground pt-2 text-center text-xs text-pretty">
        This page was shared with you by email. Anyone with the link can open it for seven days, so
        forward it with care.
      </p>
    </SharedCallShell>
  );
}
